import React, { useEffect, useMemo, useState } from "react";
import { api, fmt, stateBadgeStyleObj } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, Modal, EmptyState } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import { Search, Receipt, Banknote, Hourglass, CheckCircle2 } from "lucide-react";

const PAGE_SIZE = 15;

const AdminPanelVendorBillsPage = () => {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [view, setView] = useState(null);

  const [journals, setJournals] = useState([]);
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ journal_id: "", amount: "", payment_date: "" });
  const [paying, setPaying] = useState(false);

  useEffect(() => { setPage(1); }, [tab, search]);

  const load = async () => {
    try {
      const rows = await api.accounting.vendorBills({ limit: 500 });
      setData(rows || []);
    } catch { setData([]); }
  };

  useEffect(() => {
    (async () => {
      await load();
      try { setJournals((await api.journals.list({ limit: 500 }).catch(() => [])) || []); } catch {}
      setLoaded(true);
    })();
  }, []);

  const label = (state) => {
    if (state === "draft") return "Draft";
    if (state === "posted" || state === "done") return "Posted";
    if (state === "paid") return "Paid";
    if (state === "partial") return "Partial";
    if (state === "cancel") return "Cancelled";
    return state || "—";
  };
  const badgeKey = (state) => {
    if (state === "posted" || state === "done") return "sale";
    if (state === "paid") return "paid";
    if (state === "partial") return "invoiced";
    if (state === "cancel") return "cancel";
    return "draft";
  };
  const paymentState = (row) => {
    if (row.payment_state === "paid") return "paid";
    if (row.payment_state === "partial") return "partial";
    return row.state;
  };

  const stats = useMemo(() => {
    const total = data.reduce((s, r) => s + (Number(r.amount_total) || 0), 0);
    const open = data.reduce((s, r) => s + (Number(r.amount_residual) || 0), 0);
    const paid = data.reduce((s, r) => s + (paymentState(r) === "paid" ? (Number(r.amount_total) || 0) : 0), 0);
    return { count: data.length, total, open, paid };
  }, [data]);

  const filtered = useMemo(() => {
    return data.filter((r) => {
      if (tab !== "all" && paymentState(r) !== tab) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${r.name || r.bill_name || ""} ${r.partner_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, tab, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const tabs = [
    { key: "all", label: "All", count: data.length },
    { key: "posted", label: "Posted", count: data.filter((r) => r.state === "posted" || r.state === "done").length },
    { key: "partial", label: "Partial", count: data.filter((r) => paymentState(r) === "partial").length },
    { key: "paid", label: "Paid", count: data.filter((r) => paymentState(r) === "paid").length },
    { key: "cancel", label: "Cancelled", count: data.filter((r) => r.state === "cancel").length },
  ];

  const openPay = (row) => {
    setPayFor(row);
    const residual = Number(row.amount_residual);
    setPayForm({
      journal_id: "",
      amount: (residual > 0 ? residual : Number(row.amount_total) || 0).toString(),
      payment_date: new Date().toISOString().split("T")[0],
    });
  };

  const submitPay = async () => {
    const amount = Number(payForm.amount);
    if (!amount || amount <= 0) { toast("Enter a payment amount", "error"); return; }
    setPaying(true);
    try {
      await api.accounting.pay(payFor.id, {
        journal_id: payForm.journal_id ? parseInt(payForm.journal_id) : undefined,
        amount,
        payment_date: payForm.payment_date ? `${payForm.payment_date}T00:00:00Z` : undefined,
      });
      toast("Vendor payment registered", "success");
      setPayFor(null);
      await load();
    } catch (e) {
      toast(e.message || "Payment failed", "error");
    }
    setPaying(false);
  };

  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
    fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
  };
  const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px", display: "block" };

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader title="Vendor Bills" subtitle="Supplier invoices awaiting payment" />

      <div className="ess-sg ess-sg4" style={{ marginBottom: "24px" }}>
        <StatCard label="Bills" value={String(stats.count)} icon={Receipt} color="green" trend="Total received" />
        <StatCard label="Billed" value={fmt.moneyShort(stats.total)} icon={Banknote} color="rose" trend={fmt.money(stats.total)} />
        <StatCard label="Outstanding" value={fmt.moneyShort(stats.open)} icon={Hourglass} color="amber" trend={fmt.money(stats.open)} />
        <StatCard label="Paid" value={fmt.moneyShort(stats.paid)} icon={CheckCircle2} color="green" trend={fmt.money(stats.paid)} />
      </div>

      <div style={{ display: "flex", gap: "24px", marginBottom: "20px", borderBottom: "1px solid #e2e7e2", overflowX: "auto" }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "9px 2px", border: "none", background: "transparent",
            borderBottom: `2px solid ${tab === t.key ? "#225f2b" : "transparent"}`,
            marginBottom: -1, whiteSpace: "nowrap",
            color: tab === t.key ? "#225f2b" : "#64748b", fontSize: "13px",
            fontWeight: tab === t.key ? "600" : "500", cursor: "pointer",
            display: "flex", alignItems: "center", gap: "6px"
          }}>
            {t.label}
            <span style={{ background: tab === t.key ? "#f0f7f0" : "#f1f5f9", color: tab === t.key ? "#225f2b" : "#64748b", padding: "1px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: "600" }}>{t.count}</span>
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px", background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e7e2" }}>
        <div style={{ flex: 1, minWidth: "200px", position: "relative" }}>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search bill or vendor..." style={{ ...inputStyle, padding: "9px 12px 9px 36px" }} />
        </div>
      </div>

      <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
        {paginated.length === 0 ? (
          <EmptyState icon="🧾" title="No vendor bills" subtitle="Bills appear once you create them from a purchase order" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th className="ess-th" style={{ minWidth: "150px" }}>Bill #</th>
                  <th className="ess-th" style={{ minWidth: "160px" }}>Vendor</th>
                  <th className="ess-th" style={{ minWidth: "110px" }}>Date</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "130px" }}>Amount</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "110px" }}>Residual</th>
                  <th className="ess-th" style={{ minWidth: "100px" }}>Status</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "70px" }}></th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((row) => (
                  <tr key={row.id} className="ess-tr">
                    <td className="ess-td" style={{ fontWeight: "600", color: "#225f2b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.name || row.bill_name || "—"}</td>
                    <td className="ess-td" style={{ fontWeight: "500", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.partner_name || "—"}</td>
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{fmt.date(row.date || row.invoice_date || row.date_invoice)}</td>
                    <td className="ess-td" style={{ fontWeight: "600", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(row.amount_total)}</td>
                    <td className="ess-td" style={{ fontWeight: "500", color: paymentState(row) === "paid" ? "#16a34a" : paymentState(row) === "partial" ? "#d97706" : "#94a3b8", textAlign: "right", whiteSpace: "nowrap" }}>{paymentState(row) === "paid" ? fmt.money(0) : fmt.money(row.amount_residual)}</td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                      <span style={stateBadgeStyleObj(badgeKey(paymentState(row)))}>{label(paymentState(row))}</span>
                    </td>
                    <td className="ess-td" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button onClick={() => setView(row)} style={{ border: "none", background: "transparent", color: "#225f2b", fontSize: "13px", fontWeight: "500", cursor: "pointer", padding: "4px 8px", borderRadius: "4px" }} onMouseEnter={(e) => (e.target.style.background = "#f0f7f0")} onMouseLeave={(e) => (e.target.style.background = "transparent")}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #e2e7e2", background: "#fafafa" }}>
          <span style={{ fontSize: "13px", color: "#64748b" }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: "flex", gap: "4px" }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page <= 1 ? "#f1f5f9" : "white", color: page <= 1 ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page <= 1 ? "not-allowed" : "pointer" }}>← Prev</button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page >= totalPages ? "#f1f5f9" : "white", color: page >= totalPages ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page >= totalPages ? "not-allowed" : "pointer" }}>Next →</button>
          </div>
        </div>
      </div>

      <Modal open={!!view} onClose={() => setView(null)} title={view ? `Vendor Bill ${view.name || view.bill_name || ""}` : ""} size="lg">
        {view && (
          <div>
            <div style={{ display: "grid", gap: "16px" }} className="ess-rg-2">
              <div><label style={labelStyle}>Vendor</label><p style={{ fontSize: "14px", color: "#1e293b", fontWeight: "600" }}>{view.partner_name || "—"}</p></div>
              <div><label style={labelStyle}>Bill #</label><p style={{ fontSize: "14px", color: "#1e293b", fontWeight: "500" }}>{view.name || view.bill_name || "—"}</p></div>
              <div><label style={labelStyle}>Date</label><p style={{ fontSize: "14px", color: "#1e293b", fontWeight: "500" }}>{fmt.date(view.date || view.invoice_date || view.date_invoice)}</p></div>
              <div><label style={labelStyle}>Status</label><span style={stateBadgeStyleObj(badgeKey(paymentState(view)))}>{label(paymentState(view))}</span></div>
              {view.journal_name && <div><label style={labelStyle}>Journal</label><p style={{ fontSize: "14px", color: "#475569" }}>{view.journal_name}</p></div>}
              {view.ref && <div><label style={labelStyle}>Reference</label><p style={{ fontSize: "14px", color: "#475569" }}>{view.ref}</p></div>}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-end", marginTop: "24px", paddingTop: "16px", borderTop: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "13px", color: "#64748b" }}>Residual <span style={{ fontWeight: "600", color: paymentState(view) === "paid" ? "#16a34a" : "#e11d48", marginLeft: "12px" }}>{fmt.money(paymentState(view) === "paid" ? 0 : view.amount_residual)}</span></div>
              <div style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b" }}>Total <span style={{ marginLeft: "12px" }}>{fmt.money(view.amount_total)}</span></div>
            </div>
            <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "20px", display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              {paymentState(view) === "paid" ? (
                <p style={{ fontSize: "13px", color: "#16a34a", fontWeight: "500", padding: "10px 0" }}>This bill is fully paid.</p>
              ) : (view.state === "posted" || view.state === "done" || view.state === "partial") ? (
                <button onClick={() => openPay(view)} className="ess-btn ess-btn-primary" style={{ padding: "10px 24px" }}>Register Payment</button>
              ) : null}
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!payFor} onClose={() => setPayFor(null)} title={payFor ? `Register Payment — ${payFor.name || payFor.bill_name || ""}` : ""}>
        {payFor && (
          <div>
            <div style={{ display: "grid", gap: "16px", marginBottom: "20px" }} className="ess-rg-2">
              <div><label style={labelStyle}>Vendor</label><p style={{ fontSize: "14px", color: "#1e293b", fontWeight: "600" }}>{payFor.partner_name || "—"}</p></div>
              <div><label style={labelStyle}>Outstanding</label><p style={{ fontSize: "14px", color: "#e11d48", fontWeight: "700" }}>{fmt.money(payFor.amount_residual)}</p></div>
            </div>
            <div style={{ display: "grid", gap: "16px" }} className="ess-rg-2">
              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Payment Journal</label>
                <select value={payForm.journal_id} onChange={(e) => setPayForm((f) => ({ ...f, journal_id: e.target.value }))} style={inputStyle}>
                  <option value="">Default cash/bank journal</option>
                  {journals.map((j) => <option key={j.id} value={j.id}>{j.name || j.code}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Payment Date</label>
                <input type="date" value={payForm.payment_date} onChange={(e) => setPayForm((f) => ({ ...f, payment_date: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <div style={{ marginBottom: "20px" }}>
              <label style={labelStyle}>Amount *</label>
              <input type="number" min="0" step="0.01" value={payForm.amount} onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} style={inputStyle} />
            </div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e8f0", paddingTop: "20px" }}>
              <button onClick={() => setPayFor(null)} className="ess-btn ess-btn-secondary" style={{ padding: "10px 24px" }}>Cancel</button>
              <button onClick={submitPay} disabled={paying} className="ess-btn ess-btn-primary" style={{ padding: "10px 24px" }}>
                {paying ? "Paying…" : "Confirm Payment"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminPanelVendorBillsPage;